import { useState } from "react";
import {
  Activity,
  BarChart3,
  ChevronDown,
  ChevronRight,
  Copy,
  Globe,
  Key,
  MoreHorizontal,
  RefreshCw,
  Route,
  Server,
  Shield,
  ShieldCheck,
  Trash2,
  Upload,
  Wifi,
  WifiOff,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CertificateStatusPanel } from "./CertificateStatusPanel";

const ingestionCopy = (server) => {
  const state = server.ingestion_status || "red";
  const age = server.ingestion_age_seconds;
  const ageText = age == null ? "No click batches" : age < 90 ? "Just now" : age < 3600 ? `${Math.floor(age / 60)}m ago` : age < 86400 ? `${Math.floor(age / 3600)}h ago` : `${Math.floor(age / 86400)}d ago`;
  return {
    state,
    ageText,
    label: state === "green" ? "Ingesting" : state === "amber" ? "Clicks slow" : "No clicks",
    dot: state === "green" ? "bg-emerald-400" : state === "amber" ? "bg-amber-400" : "bg-rose-400",
  };
};

const copyIp = async (ip) => {
  try {
    await navigator.clipboard.writeText(ip);
    toast.success("Server IP copied");
  } catch (_error) {
    toast.error("Copy failed", { description: ip });
  }
};

export const ServerCard = ({
  children,
  checkingHealth,
  fingerproxySyncing,
  health,
  isDomainsOpen,
  isSetupOpen,
  onDelete,
  onHealth,
  onRebalance,
  onRegenerateKey,
  onSyncTls,
  onTestConnection,
  onToggleDomains,
  onToggleSetup,
  onViewStats,
  server,
  testingConnection,
}) => {
  const resolvedOnline = health ? ["online", "slow"].includes(health.status) : server.is_online;
  const ingestion = ingestionCopy(server);
  const latency = health?.latency_ms;
  const [certOpen, setCertOpen] = useState(false);

  return (
    <div
      className={`wx-card group min-w-0 overflow-hidden ${resolvedOnline ? "hover:border-emerald-500/40" : "border-rose-500/25 hover:border-rose-500/40"}`}
      data-testid={`server-${server.id}`}
    >
      {/* subtle accent bar — green for online, rose for offline */}
      <div className={`pointer-events-none absolute inset-x-0 top-0 h-px ${resolvedOnline ? "bg-gradient-to-r from-transparent via-emerald-400/40 to-transparent" : "bg-gradient-to-r from-transparent via-rose-400/40 to-transparent"}`} />

      {/* ---------- Header ---------- */}
      <div className="flex min-w-0 items-start gap-3">
        <div className={`flex h-11 w-11 flex-none items-center justify-center rounded-md border ${resolvedOnline ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-slate-700 bg-slate-800/60 text-slate-500"}`}>
          <Server className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <h3 className="truncate text-base font-semibold text-slate-50">{server.name}</h3>
            <span
              className={`wx-chip ${resolvedOnline ? "wx-chip-emerald" : "wx-chip-slate"}`}
              data-testid={`server-status-pill-${server.id}`}
            >
              {resolvedOnline ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
              {resolvedOnline ? "Online" : "Offline"}
            </span>
          </div>
          <button
            type="button"
            onClick={() => copyIp(server.ip_address)}
            className="mt-1 flex max-w-full items-center gap-1.5 font-mono text-xs text-slate-400 transition-colors hover:text-cyan-300"
            data-testid={`copy-server-ip-${server.id}`}
            title="Copy server IP"
          >
            <span className="truncate">{server.ip_address}</span>
            <Copy className="h-3 w-3 flex-none" />
          </button>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex h-9 items-center gap-1 rounded-md border border-slate-700 bg-slate-900/70 px-2.5 text-xs font-medium text-slate-300 transition-colors hover:border-cyan-500/40 hover:text-slate-50"
              data-testid={`server-manage-dropdown-${server.id}`}
              aria-label={`Manage ${server.name}`}
            >
              <MoreHorizontal className="h-4 w-4" />
              <span className="hidden sm:inline">Manage</span>
              <ChevronDown className="h-3 w-3" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 border-slate-700 bg-slate-900 p-1 text-slate-200 shadow-2xl">
            <DropdownMenuLabel className="text-xs uppercase text-slate-500">Server operations</DropdownMenuLabel>
            <DropdownMenuItem onSelect={onTestConnection} disabled={testingConnection} className="cursor-pointer focus:bg-slate-800 focus:text-white" data-testid={`test-connection-${server.id}`}><Activity /> Test connection</DropdownMenuItem>
            <DropdownMenuItem onSelect={onViewStats} className="cursor-pointer focus:bg-slate-800 focus:text-white" data-testid={`view-stats-${server.id}`}><BarChart3 /> View statistics</DropdownMenuItem>
            <DropdownMenuItem onSelect={onRegenerateKey} className="cursor-pointer focus:bg-slate-800 focus:text-white" data-testid={`regenerate-key-${server.id}`}><Key /> Regenerate API key</DropdownMenuItem>
            <DropdownMenuSeparator className="bg-slate-700" />
            <DropdownMenuItem onSelect={onToggleSetup} className="cursor-pointer focus:bg-slate-800 focus:text-white" data-testid={`setup-domains-${server.id}`}><Upload /> {isSetupOpen ? "Close batch setup" : "Batch setup domains"}</DropdownMenuItem>
            <DropdownMenuItem onSelect={onRebalance} className="cursor-pointer focus:bg-slate-800 focus:text-white" data-testid={`rebalance-dns-${server.id}`}><Route /> DNS rebalance</DropdownMenuItem>
            <DropdownMenuSeparator className="bg-slate-700" />
            <DropdownMenuItem onSelect={onDelete} className="cursor-pointer text-rose-300 focus:bg-rose-500/10 focus:text-rose-200" data-testid={`delete-server-${server.id}`}><Trash2 /> Delete server</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* ---------- Metric strip ---------- */}
      <div className="wx-metric-group mt-4 grid-cols-3">
        <div className="wx-metric">
          <p className="wx-metric-label">Domains</p>
          <p className="wx-metric-value" data-testid={`server-domain-count-${server.id}`}>{server.domain_count || 0}</p>
        </div>
        <div className="wx-metric">
          <p className="wx-metric-label">Requests</p>
          <p className="wx-metric-value" data-testid={`server-request-count-${server.id}`}>{(server.total_requests || 0).toLocaleString()}</p>
        </div>
        <div className="wx-metric">
          <p className="wx-metric-label">Latency</p>
          <p
            className={`wx-metric-value ${latency == null ? "text-slate-500" : latency < 250 ? "text-emerald-300" : "text-amber-300"}`}
            data-testid={`server-health-${server.id}`}
          >
            {latency == null ? "—" : `${latency}ms`}
          </p>
        </div>
      </div>

      {/* ---------- Collapsed cert section (decluttered) ---------- */}
      <button
        type="button"
        onClick={() => setCertOpen((v) => !v)}
        className="mt-4 flex w-full items-center justify-between rounded-lg border border-slate-800/60 bg-slate-950/40 px-3 py-2 text-left text-xs text-slate-300 transition-colors hover:border-slate-700 hover:bg-slate-900/60"
        data-testid={`toggle-cert-${server.id}`}
        aria-expanded={certOpen}
      >
        <span className="inline-flex items-center gap-2">
          <ShieldCheck className="h-3.5 w-3.5 text-cyan-400" />
          <span className="wx-overline">DNS &amp; Certificate</span>
        </span>
        <ChevronRight className={`h-3.5 w-3.5 text-slate-500 transition-transform ${certOpen ? "rotate-90" : ""}`} />
      </button>
      {certOpen && (
        <div className="mt-2 wx-fade-up">
          <CertificateStatusPanel server={server} />
        </div>
      )}

      {/* ---------- Ingestion + key prefix ---------- */}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs">
        <span className="inline-flex items-center gap-1.5 text-slate-400" data-testid={`server-ingestion-${server.id}`}>
          <span className={`h-2 w-2 rounded-full ${ingestion.dot} ${ingestion.state === "green" ? "animate-pulse" : ""}`} />
          {ingestion.label}
          <span className="text-slate-600">· {ingestion.ageText}</span>
        </span>
        <span className="font-mono text-slate-500" data-testid={`server-key-prefix-${server.id}`}>
          Key {server.api_key_prefix || "—"}…
        </span>
      </div>

      {/* ---------- Action row ---------- */}
      <div className="mt-4 grid grid-cols-3 gap-2">
        <button
          type="button"
          onClick={onHealth}
          disabled={checkingHealth}
          className="wx-btn border border-amber-500/30 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 text-xs"
          data-testid={`check-health-${server.id}`}
        >
          {checkingHealth ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
          <span className="truncate">Health</span>
        </button>
        <button
          type="button"
          onClick={onToggleDomains}
          className={`wx-btn text-xs ${isDomainsOpen ? "border border-cyan-400 bg-cyan-500/25 text-cyan-100" : "border border-cyan-500/30 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20"}`}
          data-testid={`manage-domains-${server.id}`}
          aria-expanded={isDomainsOpen}
        >
          <Globe className="h-3.5 w-3.5" />
          <span className="truncate">Domains</span>
        </button>
        <button
          type="button"
          onClick={onSyncTls}
          disabled={fingerproxySyncing}
          className="wx-btn border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 text-xs"
          data-testid={`fingerproxy-sync-${server.id}`}
        >
          {fingerproxySyncing ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Shield className="h-3.5 w-3.5" />}
          <span className="truncate">Sync TLS</span>
        </button>
      </div>

      {testingConnection && (
        <p className="mt-3 flex items-center gap-2 text-xs text-cyan-300" data-testid={`server-connection-loading-${server.id}`}>
          <RefreshCw className="h-3.5 w-3.5 animate-spin" />
          Testing agent connection…
        </p>
      )}
      {children}
    </div>
  );
};
